import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Loader2, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    useExportSessionNotes,
    type SessionNotesExportFilter,
    type SessionNotesNoteStatus,
} from '@/api/reports';
import { useClientNames } from '@/api/clients';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/FormField';
import { toIsoDate } from '@/lib/calendar';

/**
 * Session Notes Export — produces a Word .docx of session notes for one
 * client across a date window. Separate from the aggregate reports: the
 * payload is a full per-session disclosure (staff, times, free-text notes,
 * embedded signature images) intended for sharing with schools, insurers,
 * or families. Gated on `export_reports`; the server writes an audit
 * entry on every call.
 */

const STATUS_LABELS: Record<SessionNotesNoteStatus, string> = {
    finalized:               'Finalized only (default)',
    finalized_and_complete:  'Finalized + Complete',
    all:                     'All notes',
};

export function SessionNotesExportPage() {
    const canExport = useAuthStore((s) => s.hasPermission('export_reports'));

    const [form, setForm] = useState<SessionNotesExportFilter>({
        clientId:   '',
        dateFrom:   undefined,
        dateTo:     undefined,
        noteStatus: 'finalized',
    });

    const exportM = useExportSessionNotes();
    const clientsQuery = useClientNames();

    const clientOptions = useMemo(() => {
        if (!clientsQuery.data) return [];
        return clientsQuery.data
            .filter((c) => c.status === 'Active' || c.status === 'Waitlist' || c.status === 'Inactive')
            .map((c) => ({ id: c.id, label: `${c.last}, ${c.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [clientsQuery.data]);

    async function runExport() {
        if (!form.clientId) return;
        try {
            await exportM.mutateAsync({
                clientId:   form.clientId,
                dateFrom:   form.dateFrom || undefined,
                dateTo:     form.dateTo   || undefined,
                noteStatus: form.noteStatus,
            });
        } catch {
            /* mutation.isError renders below */
        }
    }

    const busy = exportM.isPending;
    const canSubmit = !!form.clientId && !busy;

    return (
        <div className="p-6 max-w-3xl mx-auto space-y-4">
            <div>
                <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                    <ArrowLeft className="w-3 h-3" /> All reports
                </Link>
                <h1 className="font-display text-2xl font-bold text-text">Session Notes Export</h1>
                <p className="text-xs text-text-subtle">
                    Download a Word document of session notes for one client across a date window. Shareable with schools, insurers, or families. Every export is audited.
                </p>
            </div>

            {!canExport ? (
                <Card>
                    <CardBody>
                        <p className="text-sm text-text">You don't have permission to export session notes.</p>
                        <p className="text-xs text-text-subtle mt-1">
                            Ask an admin to grant <code className="text-2xs">export_reports</code> to your role.
                        </p>
                    </CardBody>
                </Card>
            ) : (
                <Card>
                    <CardBody className="space-y-4">
                        <label className="text-xs block">
                            <span className="text-text-muted">Client <span className="text-error">*</span></span>
                            <select
                                value={form.clientId}
                                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                                disabled={clientsQuery.isLoading}
                            >
                                <option value="">— Select a client —</option>
                                {clientOptions.map((c) => (
                                    <option key={c.id} value={c.id}>{c.label}</option>
                                ))}
                            </select>
                        </label>

                        <div className="grid grid-cols-2 gap-3">
                            <label className="text-xs block">
                                <span className="text-text-muted">Date from</span>
                                <Input
                                    type="date"
                                    min="1900-01-01"
                                    max={toIsoDate(new Date())}
                                    value={form.dateFrom ?? ''}
                                    onChange={(e) => setForm({ ...form, dateFrom: e.target.value || undefined })}
                                    className="mt-1 h-8"
                                />
                            </label>
                            <label className="text-xs block">
                                <span className="text-text-muted">Date to</span>
                                <Input
                                    type="date"
                                    min={form.dateFrom || '1900-01-01'}
                                    max={toIsoDate(new Date())}
                                    value={form.dateTo ?? ''}
                                    onChange={(e) => setForm({ ...form, dateTo: e.target.value || undefined })}
                                    className="mt-1 h-8"
                                />
                            </label>
                        </div>

                        <label className="text-xs block">
                            <span className="text-text-muted">Notes to include</span>
                            <select
                                value={form.noteStatus ?? 'finalized'}
                                onChange={(e) => setForm({ ...form, noteStatus: e.target.value as SessionNotesNoteStatus })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                {(Object.keys(STATUS_LABELS) as SessionNotesNoteStatus[]).map((k) => (
                                    <option key={k} value={k}>{STATUS_LABELS[k]}</option>
                                ))}
                            </select>
                            <span className="text-2xs text-text-subtle mt-1 block">
                                Finalized notes carry a locked snapshot of the note template. Including Complete or Draft notes exports the live template shape, which may change later.
                            </span>
                        </label>

                        <div className="flex items-center gap-2 pt-2">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={runExport}
                                disabled={!canSubmit}
                            >
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                Export .docx
                            </Button>
                            {exportM.isSuccess && !busy && (
                                <span className="text-xs text-success-text inline-flex items-center gap-1">
                                    <FileText className="w-3.5 h-3.5" /> {exportM.data?.filename}
                                </span>
                            )}
                        </div>

                        {exportM.isError && (
                            <div>
                                <p className="text-sm text-error font-medium">Export failed</p>
                                <p className="text-xs text-text-subtle mt-1">
                                    {exportM.error instanceof Error ? exportM.error.message : 'Please try again.'}
                                </p>
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}
        </div>
    );
}
